import React, { useState, useRef, useEffect } from 'react';
import { Plus, Search, Upload, FileSpreadsheet, Trash2, Pencil, Loader2, Clock, Download, Map as MapIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { BRANDS } from '../../mock/mock';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { useFuels } from '../../lib/fuels';
import ScheduleEditor from '../../components/ScheduleEditor';
import StationMarkersMap from '../../components/StationMarkersMap';

const regions = ['Ciudad de Guatemala', 'Mixco', 'Villa Nueva', 'Antigua', 'Quetzaltenango', 'Santa Catarina Pinula', 'Escuintla'];
const ALL_FUELS = ['vpower', 'super', 'regular', 'diesel', 'ulsd'];
const NONE_DEPT = '__none__';
const defaultAdjustments = (ids) => (ids || ALL_FUELS).reduce((a, f) => ({ ...a, [f]: { adjustment: 0, auto_full_diff: 1.1 } }), {});
const empty = { name: '', brand: 'UNO', address: '', region: 'Ciudad de Guatemala', lat: '', lng: '', bu: '', services: { self: true, full: true }, comparable_service: null, marker_station_ids: [], min_hours_between_changes: 48, tracking_diff_min: '', gps_radius_m: '', available_products: [...ALL_FUELS], department_id: '', trade_area_id: '', product_adjustments: defaultAdjustments(ALL_FUELS), operation: '' };

const AdminStations = () => {
  const [stations, setStations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [brandNames, setBrandNames] = useState([]);
  const [ownBrands, setOwnBrands] = useState([]);
  const suggestedBuRef = useRef('');
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');         // 'all' | brand name
  const [ownershipFilter, setOwnershipFilter] = useState('all'); // 'all' | 'own' | 'competitor'
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [tradeAreas, setTradeAreas] = useState([]);
  const [scheduleStation, setScheduleStation] = useState(null);
  const [mapStation, setMapStation] = useState(null);
  const [markerQ, setMarkerQ] = useState('');
  const fileRef = useRef(null);
  const opFileRef = useRef(null);
  const masterFuels = useFuels();
  const allFuelIds = masterFuels.map(f => f.id);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/stations'), api.get('/departments'), api.get('/brands').catch(() => ({ data: [] })), api.get('/trade-areas').catch(() => ({ data: [] }))])
      .then(([s, d, b, t]) => {
        setStations(s.data);
        setDepartments(d.data || []);
        setTradeAreas(t.data || []);
        setBrandNames((b.data || []).map(x => x.name));
        setOwnBrands((b.data || []).filter(x => x.is_own).map(x => x.name.toLowerCase()));
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!open || editing) return;
    const isCompetitor = form.brand && !ownBrands.includes(form.brand.toLowerCase());
    if (!isCompetitor) return;
    api.get(`/stations/next-bu?brand=${encodeURIComponent(form.brand)}`)
      .then(r => {
        const sug = r.data?.bu;
        if (!sug) return;
        setForm(f => (f.bu === '' || f.bu === suggestedBuRef.current) ? { ...f, bu: sug } : f);
        suggestedBuRef.current = sug;
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, form.brand, ownBrands]);

  const brandOptions = brandNames.length ? brandNames : BRANDS;
  const canon = (list, v) => list.find(x => x.toLowerCase() === (v || '').trim().toLowerCase()) || (v || '');
  const withCurrent = (list, v) => (v && !list.includes(v) ? [...list, v] : list);

  const deptName = (id) => departments.find(d => d.id === id)?.name || '';

  const filtered = stations.filter(s => {
    if (q) {
      const t = q.toLowerCase();
      const inName = s.name.toLowerCase().includes(t);
      const inAddr = s.address.toLowerCase().includes(t);
      const inDept = deptName(s.department_id).toLowerCase().includes(t);
      const inBu = (s.bu || '').toLowerCase().includes(t);
      if (!(inName || inAddr || inDept || inBu)) return false;
    }
    if (brandFilter !== 'all' && (s.brand || '').toLowerCase() !== brandFilter.toLowerCase()) return false;
    if (ownershipFilter === 'own' && !s.is_own) return false;
    if (ownershipFilter === 'competitor' && s.is_own) return false;
    if (departmentFilter !== 'all') {
      if (departmentFilter === NONE_DEPT) { if (s.department_id) return false; }
      else if (s.department_id !== departmentFilter) return false;
    }
    return true;
  });

  const save = async () => {
    if (!form.name || !form.address) { toast.error('Nombre y dirección requeridos'); return; }
    if (form.bu && !/^[A-Za-z0-9-]{1,12}$/.test(form.bu)) { toast.error('BU inválido: usa números o letras (máx. 12 caracteres, sin espacios)'); return; }
    const services = form.services || { self: true, full: true };
    const onlyOne = (services.self && !services.full) || (!services.self && services.full);
    const payload = {
      ...form,
      lat: parseFloat(form.lat) || 0,
      lng: parseFloat(form.lng) || 0,
      services,
      comparable_service: onlyOne ? (services.self ? 'self' : 'full') : null,
      min_hours_between_changes: parseInt(form.min_hours_between_changes, 10) || 48,
      tracking_diff_min: form.tracking_diff_min === '' || form.tracking_diff_min == null ? null : parseFloat(form.tracking_diff_min),
      gps_radius_m: form.gps_radius_m === '' || form.gps_radius_m == null ? null : parseFloat(form.gps_radius_m),
      available_products: (form.available_products && form.available_products.length > 0)
        ? form.available_products
        : allFuelIds,
      department_id: form.department_id || null,
      trade_area_id: form.trade_area_id || null,
      product_adjustments: form.product_adjustments || {},
      operation: form.operation || null,
    };
    try {
      if (editing) {
        await api.put(`/stations/${editing}`, payload);
        toast.success('Estación actualizada');
      } else {
        await api.post('/stations', payload);
        toast.success('Estación agregada');
      }
      setOpen(false); setEditing(null); setForm(empty); setMarkerQ(''); load();
    } catch (e) { toast.error('Error al guardar'); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta estación?')) return;
    try { await api.delete(`/stations/${id}`); toast('Estación eliminada'); load(); }
    catch (e) { toast.error('Error al eliminar'); }
  };

  const openEdit = (s) => {
    setEditing(s.id);
    setForm({
      name: s.name, brand: canon(brandOptions, s.brand), address: s.address,
      region: canon(regions, s.region),
      lat: String(s.lat), lng: String(s.lng),
      bu: s.bu || '',
      services: s.services || { self: true, full: true },
      comparable_service: s.comparable_service || null,
      marker_station_ids: s.marker_station_ids || [],
      min_hours_between_changes: s.min_hours_between_changes != null ? s.min_hours_between_changes : 48,
      tracking_diff_min: s.tracking_diff_min != null ? s.tracking_diff_min : '',
      gps_radius_m: s.gps_radius_m != null ? s.gps_radius_m : '',
      available_products: (s.available_products && s.available_products.length > 0) ? s.available_products : [...allFuelIds],
      department_id: s.department_id || '',
      trade_area_id: s.trade_area_id || '',
      operation: s.operation_source === 'explicit' ? s.operation : '',
      product_adjustments: allFuelIds.reduce((a, f) => {
        const pa = (s.product_adjustments || {})[f] || {};
        return { ...a, [f]: { adjustment: pa.adjustment ?? 0, auto_full_diff: pa.auto_full_diff ?? 1.1 } };
      }, {}),
    });
    setOpen(true);
  };

  const onExcel = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    try {
      const r = await api.post('/stations/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.imported} estaciones importadas`);
      load();
    } catch (err) { toast.error('Error al importar'); }
    e.target.value = '';
  };

  const onOperationExcel = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    try {
      const r = await api.post('/stations/import-operation', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { updated, not_found, not_found_count } = r.data;
      toast.success(`Operación DO/RBA actualizada en ${updated} estación(es)`);
      if (not_found_count > 0) {
        toast.warning(`${not_found_count} sin coincidencia: ${not_found.slice(0, 5).join(', ')}${not_found_count > 5 ? '…' : ''}`, { duration: 10000 });
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al importar DO/RBA'); }
    e.target.value = '';
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/stations/sample-excel', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'plantilla_estaciones.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('No se pudo descargar la plantilla'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-[36px] leading-tight tracking-tight">Estaciones</h2>
          <p className="text-[13px] text-[#5a5a5a] mt-1">{stations.length} estaciones registradas en el sistema.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onExcel} className="hidden"/>
          <input ref={opFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onOperationExcel} className="hidden"/>
          <Button variant="outline" onClick={downloadTemplate} className="h-10 rounded-full border-[#1c1c1c]/20 text-[12.5px]" data-testid="stations-template-btn">
            <Download size={14} className="mr-1.5"/> Plantilla
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} className="h-10 rounded-full border-[#1c1c1c]/20 text-[12.5px]">
            <FileSpreadsheet size={14} className="mr-1.5"/> Importar Excel
          </Button>
          <Button variant="outline" onClick={() => opFileRef.current?.click()} className="h-10 rounded-full border-[#1c1c1c]/20 text-[12.5px]" title="Excel/CSV con columnas BU (o nombre) y tipo DO/RBA" data-testid="import-operation-btn">
            <Upload size={14} className="mr-1.5"/> Importar DO/RBA
          </Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o && !editing) { setForm({ ...empty, available_products: allFuelIds, product_adjustments: defaultAdjustments(allFuelIds) }); } if (!o) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button className="h-10 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12.5px]"><Plus size={14} className="mr-1.5"/> Nueva estación</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-[#f8f6f1] max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? 'Editar estación' : 'Nueva estación'}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Nombre</Label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="mt-1 bg-white"/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Marca</Label>
                    <Select value={form.brand} onValueChange={v=>setForm({...form,brand:v})}>
                      <SelectTrigger className="mt-1 bg-white"><SelectValue/></SelectTrigger>
                      <SelectContent>{withCurrent(brandOptions, form.brand).map(b=><SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Región</Label>
                    <Select value={form.region} onValueChange={v=>setForm({...form,region:v})}>
                      <SelectTrigger className="mt-1 bg-white"><SelectValue/></SelectTrigger>
                      <SelectContent>{withCurrent([...new Set([...regions, ...departments.map(d=>d.name)])], form.region).map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Dirección</Label><Input value={form.address} onChange={e=>setForm({...form,address:e.target.value})} className="mt-1 bg-white"/></div>
                <div>
                  <Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Departamento</Label>
                  <Select
                    value={form.department_id || NONE_DEPT}
                    onValueChange={v=>setForm({...form, department_id: v === NONE_DEPT ? '' : v})}
                  >
                    <SelectTrigger className="mt-1 bg-white" data-testid="station-department-select"><SelectValue placeholder="Sin asignar"/></SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value={NONE_DEPT}>— Sin asignar —</SelectItem>
                      {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10.5px] text-[#8a8579] mt-1">Departamento de Guatemala donde está ubicada la estación. Se usa en filtros, zonas y push de precios.</p>
                </div>
                <div>
                  <Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Trade Área</Label>
                  <Select
                    value={form.trade_area_id || NONE_DEPT}
                    onValueChange={v => setForm({ ...form, trade_area_id: v === NONE_DEPT ? '' : v })}
                  >
                    <SelectTrigger className="mt-1 bg-white" data-testid="station-ta-select"><SelectValue placeholder="Sin asignar"/></SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value={NONE_DEPT}>— Sin Trade Área —</SelectItem>
                      {tradeAreas.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.station_count ? ` · ${t.station_count} est.` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10.5px] text-[#8a8579] mt-1" data-testid="station-ta-hint">
                    {tradeAreas.length === 0
                      ? 'No hay Trade Áreas · créalas en Configuración → Trade Áreas.'
                      : 'Las estaciones del mismo Trade Área se mueven juntas: al aprobar el tracking de una, el push se envía a todas.'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Latitud</Label><Input type="number" step="any" value={form.lat} onChange={e=>setForm({...form,lat:e.target.value})} className="mt-1 bg-white"/></div>
                  <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Longitud</Label><Input type="number" step="any" value={form.lng} onChange={e=>setForm({...form,lng:e.target.value})} className="mt-1 bg-white"/></div>
                </div>
                <div>
                  <Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">BU (código de estación)</Label>
                  <Input value={form.bu} onChange={e=>setForm({...form,bu:e.target.value.replace(/[^A-Za-z0-9-]/g,'').slice(0,12)})} placeholder="700315" maxLength={12} className="mt-1 bg-white font-mono" data-testid="station-bu-input"/>
                  <p className="text-[10.5px] text-[#8a8579] mt-1" data-testid="bu-hint">
                    {!editing && form.bu && form.bu === suggestedBuRef.current
                      ? 'Sugerido automáticamente para competidora · editable'
                      : 'Números o letras · máx. 12 caracteres'}
                  </p>
                </div>
                {ownBrands.includes((form.brand || '').toLowerCase()) && (
                  <div>
                    <Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Operación (DO / RBA)</Label>
                    <Select value={form.operation || 'auto'} onValueChange={v=>setForm({...form, operation: v === 'auto' ? '' : v})}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="station-operation-select"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">— Automático (según zona TM) —</SelectItem>
                        <SelectItem value="RBA">RBA</SelectItem>
                        <SelectItem value="DO">DO (Dealer)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10.5px] text-[#8a8579] mt-1">Las DO no reciben pushes masivos RSOP (salvo que se marque "Incluir DO"). Si queda en automático: en zona TM = RBA, fuera = DO.</p>
                  </div>
                )}
                <div className="bg-white border border-[#e8e5de] rounded-lg p-3">
                  <Label className="text-[11px] uppercase tracking-wider text-[#8a8579] block mb-2">Servicios disponibles</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#f3f0e9] cursor-pointer">
                      <input type="checkbox" checked={!!form.services?.self} onChange={e=>setForm({...form,services:{...(form.services||{}),self:e.target.checked}})} className="w-4 h-4"/>
                      <span className="text-[12.5px]">Autoservicio</span>
                    </label>
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#f3f0e9] cursor-pointer">
                      <input type="checkbox" checked={!!form.services?.full} onChange={e=>setForm({...form,services:{...(form.services||{}),full:e.target.checked}})} className="w-4 h-4"/>
                      <span className="text-[12.5px]">Servicio Completo</span>
                    </label>
                  </div>
                  {(form.services?.self !== form.services?.full) && (
                    <p className="text-[10.5px] text-[#92400e] mt-2 bg-[#fef3c7] px-2 py-1 rounded">
                      Solo un servicio · al comparar contra otras marcas se usará "{form.services?.self ? 'autoservicio' : 'servicio completo'}" como precio comparable.
                    </p>
                  )}
                </div>
                {/* Productos disponibles */}
                <div className="bg-white border border-[#e8e5de] rounded-lg p-3" data-testid="available-products-section">
                  <Label className="text-[11px] uppercase tracking-wider text-[#8a8579] block mb-1">Productos y ajustes específicos</Label>
                  <p className="text-[10.5px] text-[#8a8579] mb-2">Activa los que vende esta estación. Ajuste se suma al precio final (flete, etc. · negativos para restar); Dif. Auto/Full se suma al precio de servicio completo.</p>
                  <div className="space-y-1.5">
                    {masterFuels.map(ft => {
                      const f = ft.id;
                      const checked = (form.available_products || []).includes(f);
                      const pa = form.product_adjustments?.[f] || {};
                      const setPA = (key, val) => setForm(fm => ({ ...fm, product_adjustments: { ...(fm.product_adjustments || {}), [f]: { ...(fm.product_adjustments?.[f] || {}), [key]: val } } }));
                      return (
                        <div key={f} className={`flex items-center gap-2 flex-wrap px-2 py-1.5 rounded border transition ${checked ? 'bg-[#e7eeff] border-[#3b6cff]' : 'bg-[#f8f6f1] border-transparent hover:bg-[#f3f0e9]'}`}>
                          <label className="flex items-center gap-2 cursor-pointer min-w-[92px]">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => {
                                const cur = new Set(form.available_products || []);
                                if (e.target.checked) cur.add(f); else cur.delete(f);
                                setForm({ ...form, available_products: Array.from(cur) });
                              }}
                              className="w-4 h-4"
                              data-testid={`avail-${f}`}
                            />
                            <span className="text-[12px] font-medium">{ft.name}</span>
                          </label>
                          {checked && (
                            <div className="flex items-center gap-2 ml-auto">
                              <span className="text-[10px] text-[#8a8579]">Ajuste</span>
                              <Input
                                type="number" step="0.01"
                                value={pa.adjustment ?? 0}
                                onChange={e => setPA('adjustment', parseFloat(e.target.value) || 0)}
                                className="h-7 w-[70px] bg-white text-[12px] px-2"
                                data-testid={`adj-${f}`}
                              />
                              <span className="text-[10px] text-[#8a8579] whitespace-nowrap">Dif. Auto/Full</span>
                              <Input
                                type="number" step="0.01"
                                value={pa.auto_full_diff ?? 1.1}
                                onChange={e => setPA('auto_full_diff', parseFloat(e.target.value) || 0)}
                                className="h-7 w-[70px] bg-white text-[12px] px-2"
                                data-testid={`autofull-${f}`}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {(form.available_products || []).length === 0 && (
                    <p className="text-[10.5px] text-[#991b1b] mt-2">⚠ Sin productos seleccionados. Se asumirán todos.</p>
                  )}
                </div>
                <div className="bg-white border border-[#e8e5de] rounded-lg p-3">
                  <Label className="text-[11px] uppercase tracking-wider text-[#8a8579] block mb-1">Marcadores (competidores cercanos) · máx 6</Label>
                  <p className="text-[10.5px] text-[#8a8579] mb-2">Estaciones de competencia que se mostrarán en MO-P01 para comparar precios.</p>
                  <Input
                    value={markerQ}
                    onChange={e=>setMarkerQ(e.target.value)}
                    placeholder="Buscar por nombre, marca o BU…"
                    className="bg-[#f8f6f1] text-[12px] h-8"
                    data-testid="marker-search-input"
                  />
                  <div className="flex flex-wrap gap-1.5 mt-2 min-h-[28px]">
                    {(form.marker_station_ids || []).map(mid => {
                      const ms = stations.find(x => x.id === mid);
                      if (!ms) return null;
                      return (
                        <span key={mid} className="inline-flex items-center gap-1 text-[11px] bg-[#1c1c1c] text-[#f8f6f1] px-2 py-0.5 rounded-full" data-testid={`marker-chip-${mid}`}>
                          {ms.name}
                          <button type="button" onClick={()=>setForm(f=>({...f, marker_station_ids: f.marker_station_ids.filter(x=>x!==mid)}))} className="hover:text-[#fca5a5] ml-0.5" data-testid={`marker-remove-${mid}`}>×</button>
                        </span>
                      );
                    })}
                    {(form.marker_station_ids || []).length === 0 && <span className="text-[11px] text-[#a8a397]">Sin marcadores configurados.</span>}
                  </div>
                  {markerQ && (
                    <div className="mt-2 max-h-40 overflow-y-auto border border-[#e8e5de] rounded bg-[#f8f6f1]">
                      {stations
                        .filter(s2 => s2.id !== editing && !(form.marker_station_ids || []).includes(s2.id))
                        .filter(s2 => {
                          const t = markerQ.toLowerCase();
                          return s2.name.toLowerCase().includes(t) || (s2.brand||'').toLowerCase().includes(t) || (s2.bu||'').includes(t);
                        })
                        .slice(0, 20)
                        .map(s2 => (
                          <button
                            key={s2.id}
                            type="button"
                            disabled={(form.marker_station_ids || []).length >= 6}
                            onClick={() => { setForm(f=>({...f, marker_station_ids: [...(f.marker_station_ids||[]), s2.id]})); setMarkerQ(''); }}
                            className="w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between"
                            data-testid={`marker-option-${s2.id}`}
                          >
                            <span className="truncate">{s2.name}</span>
                            <span className="text-[10px] text-[#8a8579] ml-2 shrink-0">{s2.brand}{s2.bu ? ` · ${s2.bu}` : ''}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <div className="bg-white border border-[#e8e5de] rounded-lg p-3">
                  <Label className="text-[11px] uppercase tracking-wider text-[#8a8579] block mb-1">Período de espera para reportar precios</Label>
                  <p className="text-[10.5px] text-[#8a8579] mb-2">Horas que deben pasar desde el último cambio antes de poder enviar un nuevo tracking sin confirmación.</p>
                  <div className="flex gap-2 items-center">
                    {[24, 48, 72].map(h => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, min_hours_between_changes: h }))}
                        className={`px-3 py-1.5 rounded-full text-[12px] transition border ${Number(form.min_hours_between_changes) === h ? 'bg-[#1c1c1c] text-[#f8f6f1] border-[#1c1c1c]' : 'bg-[#f8f6f1] border-[#e8e5de] text-[#3a3a3a] hover:bg-white'}`}
                        data-testid={`window-preset-${h}`}
                      >
                        {h}h
                      </button>
                    ))}
                    <span className="text-[11px] text-[#8a8579] mx-1">o</span>
                    <Input
                      type="number"
                      min={1}
                      max={240}
                      value={form.min_hours_between_changes}
                      onChange={e => setForm(f => ({ ...f, min_hours_between_changes: e.target.value.replace(/\D/g, '') }))}
                      className="bg-[#f8f6f1] h-8 w-20 text-[12px]"
                      data-testid="window-input"
                    />
                    <span className="text-[11px] text-[#8a8579]">h</span>
                  </div>
                </div>
                <div className="bg-white border border-[#e8e5de] rounded-lg p-3">
                  <Label className="text-[11px] uppercase tracking-wider text-[#8a8579] block mb-1">Diferencia mínima para alertar (esta estación)</Label>
                  <p className="text-[10.5px] text-[#8a8579] mb-2">Las diferencias ≤ este valor no generan ventana de tracking para esta estación. Vacío = usa el parámetro global (Configuración → Parámetros). 0 = alertar cualquier diferencia.</p>
                  <div className="flex gap-2 items-center">
                    <span className="text-[12px] text-[#8a8579]">Q</span>
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      max={50}
                      value={form.tracking_diff_min}
                      onChange={e => setForm(f => ({ ...f, tracking_diff_min: e.target.value }))}
                      placeholder="global"
                      className="bg-[#f8f6f1] h-8 w-28 text-[12px] tabular-nums"
                      data-testid="station-diff-min-input"
                    />
                  </div>
                </div>
                <div className="bg-white border border-[#e8e5de] rounded-lg p-3">
                  <Label className="text-[11px] uppercase tracking-wider text-[#8a8579] block mb-1">Radio GPS para captura (esta estación)</Label>
                  <p className="text-[10.5px] text-[#8a8579] mb-2">Distancia máxima en metros para tomar la foto sin marcar anomalía. Vacío = 100 m (default). Auméntalo en sitios de riesgo (ej. Salvador Madrid) para que el capturador no deba acercarse tanto.</p>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="number"
                      step="10"
                      min={10}
                      max={5000}
                      value={form.gps_radius_m}
                      onChange={e => setForm(f => ({ ...f, gps_radius_m: e.target.value }))}
                      placeholder="100"
                      className="bg-[#f8f6f1] h-8 w-28 text-[12px] tabular-nums"
                      data-testid="station-gps-radius-input"
                    />
                    <span className="text-[12px] text-[#8a8579]">metros</span>
                  </div>
                </div>
                <p className="text-[11px] text-[#8a8579]">Tip: el botón "Plantilla" exporta todas las estaciones con columnas <span className="font-mono">name,brand,address,region,department,lat,lng,bu,marker_bus,products</span> — edita y re-importa (upsert por BU). <span className="font-mono">products</span> con ids separados por <span className="font-mono">;</span> (ej. super;regular;diesel).</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>{setOpen(false); setEditing(null); setForm(empty);}}>Cancelar</Button>
                <Button onClick={save} className="bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black">{editing ? 'Guardar cambios' : 'Crear estación'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
          <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar estación…" className="pl-9 bg-white border-[#e8e5de] rounded-full text-[13px] h-10" data-testid="stations-search"/>
        </div>
        <div className="inline-flex items-center bg-white border border-[#e8e5de] rounded-full p-1 gap-1">
          {[
            { v: 'all', label: 'Todas' },
            { v: 'own', label: 'Propias' },
            { v: 'competitor', label: 'Competencia' },
          ].map(opt => (
            <button
              key={opt.v}
              onClick={() => setOwnershipFilter(opt.v)}
              className={`px-3 py-1.5 rounded-full text-[12px] transition ${ownershipFilter === opt.v ? 'bg-[#1c1c1c] text-[#f8f6f1]' : 'text-[#5a5a5a] hover:bg-[#f3f0e9]'}`}
              data-testid={`filter-ownership-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <Select value={brandFilter} onValueChange={setBrandFilter}>
          <SelectTrigger className="h-10 w-44 bg-white border-[#e8e5de] rounded-full text-[12.5px]" data-testid="filter-brand">
            <SelectValue placeholder="Filtrar por marca"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las marcas</SelectItem>
            {brandOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
          <SelectTrigger className="h-10 w-52 bg-white border-[#e8e5de] rounded-full text-[12.5px]" data-testid="filter-department">
            <SelectValue placeholder="Filtrar por departamento"/>
          </SelectTrigger>
          <SelectContent className="max-h-60">
            <SelectItem value="all">Todos los departamentos</SelectItem>
            <SelectItem value={NONE_DEPT}>— Sin asignar —</SelectItem>
            {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[11.5px] text-[#8a8579] ml-auto" data-testid="filter-count">
          {filtered.length} de {stations.length}
        </span>
      </div>

      <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
        {loading ? <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div> : (
          <table className="w-full text-[13px]">
            <thead className="bg-[#f8f6f1]">
              <tr className="text-left text-[11px] tracking-wider uppercase text-[#8a8579] border-b border-[#e8e5de]">
                <th className="py-3 px-4 font-normal">Nombre</th>
                <th className="py-3 px-2 font-normal">Marca</th>
                <th className="py-3 px-2 font-normal">BU</th>
                <th className="py-3 px-2 font-normal">Servicios</th>
                <th className="py-3 px-2 font-normal">Región</th>
                <th className="py-3 px-2 font-normal">Departamento</th>
                <th className="py-3 px-2 font-normal">Horario</th>
                <th className="py-3 px-4 font-normal text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e8e5de]">
              {filtered.map(s => (
                <tr key={s.id} className="hover:bg-[#fafaf6]">
                  <td className="py-3 px-4">
                    <p className="font-medium">{s.name}</p>
                    <p className="text-[11px] text-[#8a8579] truncate max-w-[260px]">{s.address}</p>
                  </td>
                  <td className="py-3 px-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded ${['Shell','UNO'].includes(s.brand) ? 'bg-[#1c1c1c] text-[#f8f6f1]' : 'bg-[#f3f0e9] text-[#3a3a3a]'}`}>{s.brand}</span>
                    {['Shell','UNO'].includes(s.brand) && (
                      <p className={`text-[9px] mt-0.5 ${s.operation === 'DO' ? 'text-[#b45309]' : 'text-[#15803d]'}`} data-testid={`station-op-${s.id}`}>
                        propia{s.operation ? ` · ${s.operation}${s.operation_source === 'derived' ? ' (auto)' : ''}` : ''}
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-2 font-mono text-[11px]">{s.bu || <span className="text-[#a8a397]">—</span>}</td>
                  <td className="py-3 px-2 text-[11px]">
                    {s.services?.self && s.services?.full ? <span>Auto + Completo</span> :
                     s.services?.self ? <span>Autoservicio</span> :
                     s.services?.full ? <span>Servicio Completo</span> :
                     <span className="text-[#a8a397]">—</span>}
                  </td>
                  <td className="py-3 px-2 text-[#3a3a3a]">{s.region}</td>
                  <td className="py-3 px-2 text-[#3a3a3a]">
                    {s.department_id ? deptName(s.department_id) : <span className="text-[#a8a397]">—</span>}
                    {s.trade_area_id && (
                      <p className="text-[9.5px] mt-0.5">
                        <span className="px-1.5 py-0.5 rounded bg-[#e7eeff] text-[#0f1d6f]" data-testid={`station-ta-tag-${s.id}`}>
                          TA: {tradeAreas.find(t => t.id === s.trade_area_id)?.name || '—'}
                        </span>
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-2">
                    {s.tracking_enabled !== false ? (
                      <button onClick={() => setScheduleStation(s)} className="inline-flex items-center gap-1 text-[11px] text-[#1c1c1c] hover:underline">
                        <Clock size={11}/> {Object.values(s.schedule || {}).flat().length} slots
                      </button>
                    ) : (
                      <span className="text-[11px] text-[#8a8579]">Pausado</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex gap-1">
                      {s.is_own && (
                        <button onClick={()=>setMapStation(s)} className="w-7 h-7 rounded hover:bg-[#e7eeff] flex items-center justify-center text-[#0f1d6f]" title="Ver mapa de markers" data-testid={`station-map-btn-${s.id}`}><MapIcon size={12}/></button>
                      )}
                      <button onClick={()=>setScheduleStation(s)} className="w-7 h-7 rounded hover:bg-[#f3f0e9] flex items-center justify-center" title="Horario"><Clock size={12}/></button>
                      <button onClick={()=>openEdit(s)} className="w-7 h-7 rounded hover:bg-[#f3f0e9] flex items-center justify-center" title="Editar"><Pencil size={12}/></button>
                      <button onClick={()=>remove(s.id)} className="w-7 h-7 rounded hover:bg-[#fee2e2] flex items-center justify-center text-[#991b1b]" title="Eliminar"><Trash2 size={12}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ScheduleEditor
        open={!!scheduleStation}
        onOpenChange={(o) => !o && setScheduleStation(null)}
        station={scheduleStation}
        onSaved={load}
      />
      {mapStation && (
        <StationMarkersMap station={mapStation} onClose={() => setMapStation(null)} onChanged={load} />
      )}
    </div>
  );
};

export default AdminStations;
